import React, { useState, useEffect, useMemo } from 'react';
import { deleteReportById, downloadReportFile, generateReport, getAllReportsDetails, sendReportEmail, getTherapistsList } from '../../redux/actions/reportActions';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Grid, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import './Styles.scss';
import { DataGrid } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle'; 
import DeleteIcon from '@mui/icons-material/Delete'; 
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ConfirmDialog from '../ConfirmDialog';
import { getFileExtension, validateEmails } from '../../utils/helperFuntions';
import PageLoaderComp from '../pageLoaderComp';
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  ...theme.applyStyles('dark', {
    backgroundColor: '#1A2027',
  }),
}));

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// Snap a value to the nearest valid page size option
const snapToPageSizeOption = (value) => {
  return PAGE_SIZE_OPTIONS.reduce((prev, curr) =>
    Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
  );
};

// Calculate optimal page size based on viewport height
const calculatePageSize = () => {
  const viewportHeight = window.innerHeight;
  const headerHeight = 64; // AppBar height
  const footerHeight = 52; // Pagination height
  const otherElementsHeight = 200; // Title, buttons, spacing, margins (optimized for better fit)
  const rowHeight = 52; // Approximate DataGrid row height
  const columnHeaderHeight = 56; // DataGrid column header height
  
  // Calculate available height for rows (excluding header, footer, other elements, and column header)
  const availableHeight = viewportHeight - headerHeight - footerHeight - otherElementsHeight - columnHeaderHeight;
  const calculatedRows = Math.floor(availableHeight / rowHeight);
  
  // Add 1-2 extra rows since we have some buffer space
  const adjustedRows = calculatedRows + 1;
  
  // Snap to the nearest valid pageSizeOption so the dropdown renders correctly
  return snapToPageSizeOption(Math.max(10, Math.min(adjustedRows, 100)));
};

// Get initial page size from localStorage or calculate
const getInitialPageSize = () => {
  const savedPageSize = localStorage.getItem('reportPageSize');
  if (savedPageSize) {
    const parsed = parseInt(savedPageSize, 10);
    if (PAGE_SIZE_OPTIONS.includes(parsed)) {
      return parsed;
    }
  }
  return calculatePageSize();
};

const ReportActionCell = ({ params, user, onActionClick, pageLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isAdmin = user && user.role === 'ROLE_ADMIN';
  const [anchorEl, setAnchorEl] = useState(null);
  const [openEl, setOpenEl] = useState(false);

  const handleAction = (option) => {
    if (!pageLoading) {
      onActionClick(null, option, params.row);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenEl(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setOpenEl(false);
  };

  const handleMenuAction = (option) => {
    handleAction(option);
    handleMenuClose();
  };

  // Filter options based on user role - Delete only for ROLE_ADMIN
  const options = [
    { label: "Download", icon: <FileDownloadIcon sx={{ color: 'green', fontSize: 20 }} />, available: true },
    { label: "Email", icon: <EmailIcon sx={{ fontSize: 20 }} />, available: true },
    { label: "Delete", icon: <DeleteIcon sx={{ color: 'red', fontSize: 20 }} />, available: isAdmin },
  ].filter(option => option.available);

  // Mobile view: Show menu
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <IconButton
          aria-label="more"
          id={`action-menu-${params.row.id}`}
          aria-controls={openEl ? `action-menu-list-${params.row.id}` : undefined}
          aria-expanded={openEl ? 'true' : undefined}
          aria-haspopup="true"
          onClick={handleMenuClick}
          disabled={pageLoading}
          sx={{ 
            minWidth: '44px', // Touch-friendly
            minHeight: '44px'
          }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          id={`action-menu-list-${params.row.id}`}
          MenuListProps={{
            'aria-labelledby': `action-menu-${params.row.id}`,
          }}
          anchorEl={anchorEl}
          open={openEl}
          onClose={handleMenuClose}
          slotProps={{
            paper: {
              style: {
                maxHeight: 48 * 4.5,
                width: '20ch',
              },
            },
          }}
        >
          {options.map((option) => (
            <MenuItem 
              key={`${params.row.id}-${option.label}`}
              sx={{
                pointerEvents: pageLoading ? 'none' : 'auto',
              }}
              onClick={() => handleMenuAction(option.label)}
            >
              {option.icon}
              <Box sx={{ ml: 1 }}>{option.label}</Box>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  }

  // Desktop view: Show direct icons
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
      {/* Download Icon - Available to all users */}
      <Tooltip title="Download" arrow>
        <span>
          <IconButton
            aria-label="download"
            onClick={() => handleAction("Download")}
            disabled={pageLoading}
            sx={{ 
              color: 'green',
              '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' },
              minWidth: '40px',
              minHeight: '40px'
            }}
          >
            <FileDownloadIcon />
          </IconButton>
        </span>
      </Tooltip>

      {/* Email Icon - Available to all users */}
      <Tooltip title="Send Email" arrow>
        <span>
          <IconButton
            aria-label="email"
            onClick={() => handleAction("Email")}
            disabled={pageLoading}
            sx={{ 
              color: 'primary.main',
              '&:hover': { backgroundColor: 'rgba(139, 175, 145, 0.1)' },
              minWidth: '40px',
              minHeight: '40px'
            }}
          >
            <EmailIcon />
          </IconButton>
        </span>
      </Tooltip>

      {/* Delete Icon - Only for ROLE_ADMIN */}
      {isAdmin && (
        <Tooltip title="Delete" arrow>
          <span>
            <IconButton
              aria-label="delete"
              onClick={() => handleAction("Delete")}
              disabled={pageLoading}
              sx={{ 
                color: 'error.main',
                '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' },
                minWidth: '40px',
                minHeight: '40px'
              }}
            >
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

function ReportComp() {
  const [paginationModel, setPaginationModel] = useState(() => ({
    page: 0,
    pageSize: getInitialPageSize()
  }));
  const dispatch = useDispatch();
  const [pageLoading, setPageLoading] = useState(false);
  const { auth_token, user } = useSelector((state) => state.auth);
  const { all_reports, totalElements, therapists } = useSelector((state) => state.report);
  
  // Filter state - using therapistId for filtering (backend will use ID)
  const [selectedTherapistId, setSelectedTherapistId] = useState(null);
  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [selectedToDate, setSelectedToDate] = useState(null);
  
  // Filter modal state
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [filterTherapistId, setFilterTherapistId] = useState(null);
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  
  // Generate Report modal state
  const [openGenerateModal, setOpenGenerateModal] = useState(false);
  const [generateTherapistId, setGenerateTherapistId] = useState(null);
  const [generateFromDate, setGenerateFromDate] = useState('');
  const [generateToDate, setGenerateToDate] = useState('');
  
  const [formDataEmail, setFormDataEmail] = useState({
    "id":"",
    "emailAddresses": [],
    "emailAddressesString": "",
    "ccAddresses": [],
    "ccAddressesString": "",
    "subject": "",
    "message": "",
  });
  const [openEmail, setOpenEmail] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [showGenerateAllConfirmation, setShowGenerateAllConfirmation] = useState(false);
  const [buttonLoadingDelete, setButtonLoadingDelete] = useState(false);
  
  const [isSubmittedSendEmail, setIsSubmittedSendEmail] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [buttonLoadingGenRep, setButtonLoadingGenRep] = useState(false);
  
  const [deleteId, setDeleteId] = useState(null);
  // const [openEl, setOpenEl] = useState(false);
  // const openEl = Boolean(anchorEl);
  /* const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
      setOpenEl(true);
  };
  const handleCloseEl = () => {
      setAnchorEl(null);
      setOpenEl(false);
  }; */
  const handleActionClick = (evt, option, rowData) => {
      if(option === "Delete"){
          setDeleteId(rowData.id);
          setConfirmDialogOpen(true);
      }else if(option === "Email"){
        handleClickOpenEmail(rowData)
      }else if(option === "Download"){
        handleClickDownloadReport(rowData.id)
      }
  };
  const handleClickDownloadReport = async (id) => {
    setPageLoading(true);
    let response = await dispatch(downloadReportFile(id));
    if(response && response.blob){
      // Use filename from API response if available, otherwise fall back to default
      let filename = response.filename;
      
      // If no filename from API, construct one using mime type
      if (!filename) {
        const mimeType = response.mimeType || response.blob.type;
        const extension = getFileExtension(mimeType);
        filename = `report_file_${id}.${extension}`;
      }
      
       // Create a link element for downloading the file
       const url = window.URL.createObjectURL(new Blob([response.blob]));
       
       const link = document.createElement("a");
       link.href = url;
       link.setAttribute("download", filename);
       document.body.appendChild(link);
       link.click();
       // Clean up
       link.parentNode.removeChild(link);
       window.URL.revokeObjectURL(url);  
    }     
    setPageLoading(false);
  };
  
  
  const handleConfirmDelete = async () => {
    setButtonLoadingDelete(true);
    await dispatch(deleteReportById(deleteId));
    
    // Refresh reports with current pagination and filter
    await dispatch(getAllReportsDetails(
      paginationModel.page, 
      paginationModel.pageSize, 
      selectedTherapistId,
      selectedFromDate,
      selectedToDate
    ));
    setButtonLoadingDelete(false);
    setConfirmDialogOpen(false);
  };
  
  // Filter modal handlers
  const handleOpenFilterModal = () => {
    // Set modal state to current filter values
    setFilterTherapistId(selectedTherapistId);
    setFilterFromDate(selectedFromDate || '');
    setFilterToDate(selectedToDate || '');
    setOpenFilterModal(true);
  };
  
  const handleCloseFilterModal = () => {
    setOpenFilterModal(false);
  };
  
  const handleFilterSubmit = async () => {
    // Validate date range
    if (filterFromDate && filterToDate && filterFromDate > filterToDate) {
      alert('Created From date cannot be after Created To date');
      return;
    }
    
    // Apply filters
    setSelectedTherapistId(filterTherapistId);
    setSelectedFromDate(filterFromDate || null);
    setSelectedToDate(filterToDate || null);
    
    // Reset pagination to first page
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    // Close modal
    setOpenFilterModal(false);
  };
  
  // Generate Report modal handlers
  const handleOpenGenerateModal = () => {
    // Reset modal state
    setGenerateTherapistId(null);
    setGenerateFromDate('');
    setGenerateToDate('');
    setOpenGenerateModal(true);
  };
  
  const handleCloseGenerateModal = () => {
    setOpenGenerateModal(false);
  };
  
  const handleGenerateReportSubmit = async () => {
    // Check if nothing is selected (no therapist, no dates)
    if (!generateTherapistId && !generateFromDate && !generateToDate) {
      // Show confirmation dialog
      setShowGenerateAllConfirmation(true);
      return;
    }
    
    // Validate date range
    if (generateFromDate && generateToDate && generateFromDate > generateToDate) {
      alert('End date cannot be before Start date');
      return;
    }
    
    // Proceed with report generation
    await proceedWithGenerateReport();
  };
  
  const proceedWithGenerateReport = async () => {
    setShowGenerateAllConfirmation(false);
    setButtonLoadingGenRep(true);
    const data = await dispatch(generateReport(
      generateTherapistId,
      generateFromDate || null,
      generateToDate || null
    ));
    setButtonLoadingGenRep(false);
    
    if(data && data.success){
      // Refresh reports with current pagination, reset to first page, and preserve filter
      await dispatch(getAllReportsDetails(
        0, 
        paginationModel.pageSize, 
        selectedTherapistId,
        selectedFromDate,
        selectedToDate
      ));
      handleCloseGenerateModal();
    }
  };
  
  const handleCancelGenerateAll = () => {
    setShowGenerateAllConfirmation(false);
  };
  
  const generateReportFunc = async () => {
    // This function is kept for backward compatibility but should not be used
    // Use handleGenerateReportSubmit instead
    handleOpenGenerateModal();
  };
  
  const handleCancelDelete = () => {
  setConfirmDialogOpen(false);
  };

  // Handle pagination changes
  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
    // Save page size preference to localStorage
    localStorage.setItem('reportPageSize', newModel.pageSize.toString());
  };

  // Fetch therapists list on component mount (only once)
  useEffect(() => {
    if (auth_token && therapists.length === 0) {
      dispatch(getTherapistsList());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth_token]); // Only fetch when auth_token is available, not on every render

  // Fetch reports when pagination or filter changes
  useEffect(() => {
    if (!auth_token) return;
    
    let isMounted = true;
    const fetchReports = async () => {
      setPageLoading(true);
      try {
        await dispatch(
          getAllReportsDetails(
            paginationModel.page, 
            paginationModel.pageSize, 
            selectedTherapistId,
            selectedFromDate,
            selectedToDate
          )
        );
      } finally {
        if (isMounted) {
          setPageLoading(false);
        }
      }
    };
    
    fetchReports();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth_token, paginationModel.page, paginationModel.pageSize, selectedTherapistId, selectedFromDate, selectedToDate]);

  // Recalculate page size on window resize
  useEffect(() => {
    const handleResize = () => {
      const newPageSize = calculatePageSize();
      if (newPageSize !== paginationModel.pageSize) {
        setPaginationModel(prev => ({
          ...prev,
          pageSize: newPageSize
        }));
        localStorage.setItem('reportPageSize', newPageSize.toString());
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [paginationModel.pageSize]);
 
  const handleClickOpenEmail = (rowData) => {
    setOpenEmail(true);
    setFormDataEmail({
      "id": rowData.id,
      "emailAddresses": [],
      "emailAddressesString": "",
      "ccAddresses": [],
      "ccAddressesString": "",
      "subject": "",
      "message": "",
    });
    setIsSubmittedSendEmail(false);
  };
  const handleCloseEmail = () => {
    setOpenEmail(false);
    // Reset form data when modal closes
    setFormDataEmail({
      "id": "",
      "emailAddresses": [],
      "emailAddressesString": "",
      "ccAddresses": [],
      "ccAddressesString": "",
      "subject": "",
      "message": "",
    });
    setIsSubmittedSendEmail(false);
  };
  
  const handleChangeSendEmail = (event) => {
    let valueSet = event.target.value;
    const fieldName = event.target.name;
    
    // Handle email addresses (To field)
    if(fieldName === "emailAddresses" && event.target.value.length > 0){
      let emailString = event.target.value;
      const emails = emailString
      .split(/[\s,]+/)   // Split by any space or comma (one or more)
      .map((email) => email.trim())  // Trim spaces around the emails
      .filter((email) => email !== '');  // Remove empty strings
      valueSet = emails;
      setFormDataEmail({
        ...formDataEmail,
        [fieldName]: valueSet,
        "emailAddressesString": event.target.value,
      });
    }
    // Handle CC addresses
    else if(fieldName === "ccAddresses" && event.target.value.length > 0){
      let emailString = event.target.value;
      const emails = emailString
        .split(/[\s,]+/)   // Split by any space or comma (one or more)
        .map((email) => email.trim())  // Trim spaces around the emails
        .filter((email) => email !== '');  // Remove empty strings
      valueSet = emails;
      setFormDataEmail({
        ...formDataEmail,
        [fieldName]: valueSet,
        "ccAddressesString": event.target.value,
      });
    }
    // Handle empty email/CC fields
    else if(fieldName === "emailAddresses" && event.target.value.length === 0){
      setFormDataEmail({
        ...formDataEmail,
        [fieldName]: [],
        "emailAddressesString": "",
      });
    }
    else if(fieldName === "ccAddresses" && event.target.value.length === 0){
      setFormDataEmail({
        ...formDataEmail,
        [fieldName]: [],
        "ccAddressesString": "",
      });
    }
    // Handle other fields (subject, message)
    else{
      setFormDataEmail({
        ...formDataEmail,
        [fieldName]: valueSet,
      });
    }
  };

  const handleSubmitEmail = async () => {
    setIsSubmittedSendEmail(true);
    
    // Validate To emails
    const hasValidToEmails = formDataEmail.emailAddresses && 
                             formDataEmail.emailAddresses.length > 0 && 
                             validateEmails(formDataEmail.emailAddresses);
    
    // Validate CC emails if provided
    const hasValidCcEmails = !formDataEmail.ccAddresses || 
                             formDataEmail.ccAddresses.length === 0 || 
                             validateEmails(formDataEmail.ccAddresses);
    
    if(hasValidToEmails && hasValidCcEmails && formDataEmail.subject && formDataEmail.message){
      setButtonLoading(true);
      const data = await dispatch(sendReportEmail(formDataEmail));  
      
      setButtonLoading(false);
      if(data && data.success){
        handleCloseEmail(); // Use handleCloseEmail to reset form
      }
    }
  };
  

  const getColumns = (user, pageLoading, handleActionClick) => [
    { field: 'name', headerName: 'Report name', flex: 1, headerClassName: 'bold-header' },
    { field: 'therapistName', headerName: 'Therapist Name', flex: 1, headerClassName: 'bold-header' },
    { field: 'fullName', headerName: 'Created By', flex: 1, headerClassName: 'bold-header' },
    { field: 'createdAt', headerName: 'Created At', flex: 1, headerClassName: 'bold-header' },
    { 
      field: 'action',
      headerName: 'Action', 
      width: 140,
      headerClassName: 'bold-header action-column-header',
      cellClassName: 'action-column-cell',
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <ReportActionCell
          params={params}
          user={user}
          onActionClick={handleActionClick}
          pageLoading={pageLoading}
        />
      )
    },
  ];

  const columns = getColumns(user, pageLoading, handleActionClick);
  
  // Get therapist options from API response
  // API returns: [{ therapistId: 164, therapistName: "ADMIN, HTG" }, ...]
  // Keep full objects for filtering by ID while displaying names
  const therapistOptions = useMemo(() => {
    if (!therapists || therapists.length === 0) return [];
    return therapists
      .filter(therapist => therapist.therapistName && therapist.therapistId)
      .sort((a, b) => a.therapistName.localeCompare(b.therapistName));
  }, [therapists]);

  // Get selected therapist name for display
  const selectedTherapistName = useMemo(() => {
    if (!selectedTherapistId) return null;
    const therapist = therapistOptions.find(t => t.therapistId === selectedTherapistId);
    return therapist ? therapist.therapistName : null;
  }, [selectedTherapistId, therapistOptions]);

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedTherapistId) count++;
    if (selectedFromDate) count++;
    if (selectedToDate) count++;
    return count;
  }, [selectedTherapistId, selectedFromDate, selectedToDate]);
  
  const handleClearFilters = () => {
    setSelectedTherapistId(null);
    setSelectedFromDate(null);
    setSelectedToDate(null);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };
  
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Format date for display
  // Parse date string directly to avoid timezone conversion issues
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    // Parse YYYY-MM-DD format directly without timezone conversion
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  
  /* const rows = [
    { "id": 1, "reportName": "Snow Report", "createDate": "2024-11-25", "created_by": 14 },
    { "id": 2, "reportName": "Rain Analysis", "createDate": "2024-11-24", "created_by": 21 },
    { "id": 3, "reportName": "Wind Speed Study", "createDate": "2024-11-23", "created_by": 14 },
    { "id": 4, "reportName": "Temperature Trends", "createDate": "2024-11-22", "created_by": 18 },
    { "id": 5, "reportName": "Humidity Insights", "createDate": "2024-11-21", "created_by": 19 },
    { "id": 6, "reportName": "Climate Change Impact", "createDate": "2024-11-20", "created_by": 25 },
    { "id": 7, "reportName": "Air Quality Index", "createDate": "2024-11-19", "created_by": 22 },
    { "id": 8, "reportName": "Flood Risk Assessment", "createDate": "2024-11-18", "created_by": 14 },
    { "id": 9, "reportName": "Drought Monitoring", "createDate": "2024-11-17", "created_by": 20 },
    { "id": 10, "reportName": "Wildfire Analysis", "createDate": "2024-11-16", "created_by": 18 }
  ]; */
  return (
    <>
    <ConfirmDialog
        open={confirmDialogOpen}
        title="Confirm Delete"
        message="Are you sure you want to Delete?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        buttonLoading={buttonLoadingDelete}
        
      />
      
    <ConfirmDialog
        open={showGenerateAllConfirmation}
        title="Generate Report for All Therapists"
        message="Are you sure you want to generate a report for all therapists? This may take some time."
        onConfirm={proceedWithGenerateReport}
        onCancel={handleCancelGenerateAll}
        buttonLoading={buttonLoadingGenRep}
      />
      
    {/* {pageLoading ?
      <PageLoaderComp />
    : */}
      <Box className="rightContainer">
        {pageLoading ?
        <PageLoaderComp />
        :""}
      <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
              <Typography 
                component="h1" 
                variant="h5" 
                mb={2}
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
              >
              Report
              </Typography>
          </Grid>
          {user && user.role === 'ROLE_ADMIN' && (
            <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', sm: 'flex-end' }, flexWrap: 'wrap' }}>
                  <Button
                    variant={'outlined'}
                    startIcon={<FilterListIcon />}
                    onClick={handleOpenFilterModal}
                    sx={{ 
                      minHeight: { xs: '44px', sm: 'auto' } // Touch-friendly
                    }}
                  > 
                    Filter
                  </Button>
                  <Button
                    variant={'contained'}
                    onClick={handleOpenGenerateModal}
                    sx={{ 
                      minHeight: { xs: '44px', sm: 'auto' } // Touch-friendly
                    }}
                  > 
                    Generate Report
                  </Button>
                </Box>
            </Grid>
          )}
      </Grid>

      {/* Active Filters Display - Only visible to ROLE_ADMIN */}
      {user && user.role === 'ROLE_ADMIN' && activeFilterCount > 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            Active filters:
          </Typography>
          {selectedTherapistName && (
            <Chip
              label={`Therapist: ${selectedTherapistName}`}
              onDelete={() => {
                setSelectedTherapistId(null);
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {selectedFromDate && (
            <Chip
              label={`From: ${formatDateForDisplay(selectedFromDate)}`}
              onDelete={() => {
                setSelectedFromDate(null);
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {selectedToDate && (
            <Chip
              label={`To: ${formatDateForDisplay(selectedToDate)}`}
              onDelete={() => {
                setSelectedToDate(null);
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          <Button
            variant="text"
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            sx={{ ml: 1 }}
          >
            Clear All
          </Button>
        </Box>
      )}


        <Paper sx={{ 
          height: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 300px)' }, 
          width: '100%',
          overflow: 'auto' // Enable horizontal scroll on mobile
        }}>
        <DataGrid
          rows={all_reports || []}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          rowCount={totalElements || 0}
          paginationMode="server"
          autoPageSize={false}
          checkboxSelection={false}
          loading={pageLoading}
          sx={{
            // Mobile responsive adjustments
            '& .MuiDataGrid-cell': {
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '8px', sm: '16px' }
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: 'rgba(0, 0, 0, 0)',
              color: 'black',
              fontSize: { xs: '0.875rem', sm: '1rem', md: '16px' },
              padding: { xs: '8px', sm: '16px' }
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#dee7de',
              color: 'black',
              fontSize: { xs: '0.875rem', sm: '1rem', md: '16px' },
            },
            '& .MuiDataGrid-row--borderBottom': {
              backgroundColor: 'rgba(0, 0, 0, 0) !important',
              color: 'black',
              fontSize: { xs: '0.875rem', sm: '1rem', md: '16px' },
            },
            // Enable horizontal scroll on mobile
            '& .MuiDataGrid-root': {
              minWidth: { xs: '600px', sm: 'auto' } // Minimum width for horizontal scroll
            },
            border: 0,
          }}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {activeFilterCount > 0 ? 'No reports match your filter' : 'No reports found'}
                </Typography>
              </Box>
            ),
          }}
        />
        </Paper>
        
      </Box>
    {/* } */}
    
    {/* FILTER MODAL */}
    <Dialog
        open={openFilterModal}
        onClose={handleCloseFilterModal}
        maxWidth={"sm"}
        fullWidth={true}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
          },
        }}
      >
        <Box sx={{padding:'10px'}} >
        <DialogTitle>Filter Reports</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            {/* Therapist Dropdown */}
            <FormControl fullWidth>
              <InputLabel id="filter-therapist-label">Therapist</InputLabel>
              <Select
                labelId="filter-therapist-label"
                id="filter-therapist"
                value={filterTherapistId || ''}
                label="Therapist"
                onChange={(e) => setFilterTherapistId(e.target.value === '' ? null : Number(e.target.value))}
              >
                <MenuItem value="">
                  <em>All Therapists</em>
                </MenuItem>
                {therapistOptions.map((therapist) => (
                  <MenuItem key={therapist.therapistId} value={therapist.therapistId}>
                    {therapist.therapistName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Created From Date */}
            <TextField
              id="filter-from-date"
              label="Created From"
              type="date"
              value={filterFromDate}
              onChange={(e) => {
                const selectedDate = e.target.value;
                if (selectedDate && selectedDate <= getTodayDateString()) {
                  setFilterFromDate(selectedDate);
                } else if (!selectedDate) {
                  setFilterFromDate('');
                }
              }}
              inputProps={{
                max: getTodayDateString(),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />

            {/* Created To Date */}
            <TextField
              id="filter-to-date"
              label="Created To"
              type="date"
              value={filterToDate}
              onChange={(e) => {
                const selectedDate = e.target.value;
                if (selectedDate && selectedDate <= getTodayDateString()) {
                  setFilterToDate(selectedDate);
                } else if (!selectedDate) {
                  setFilterToDate('');
                }
              }}
              inputProps={{
                max: getTodayDateString(),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant={'outlined'} onClick={handleCloseFilterModal}>Cancel</Button>
          <Button variant={'contained'} onClick={handleFilterSubmit}>Apply Filter</Button>
        </DialogActions>
        </Box>
    </Dialog>

    {/* GENERATE REPORT MODAL */}
    <Dialog
        open={openGenerateModal}
        onClose={handleCloseGenerateModal}
        maxWidth={"sm"}
        fullWidth={true}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
          },
        }}
      >
        <Box sx={{padding:'10px'}} >
        <DialogTitle>Generate Report</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            {/* Therapist Dropdown */}
            <FormControl fullWidth>
              <InputLabel id="generate-therapist-label">Therapist</InputLabel>
              <Select
                labelId="generate-therapist-label"
                id="generate-therapist"
                value={generateTherapistId || ''}
                label="Therapist"
                onChange={(e) => setGenerateTherapistId(e.target.value === '' ? null : Number(e.target.value))}
              >
                <MenuItem value="">
                  <em>All Therapists</em>
                </MenuItem>
                {therapistOptions.map((therapist) => (
                  <MenuItem key={therapist.therapistId} value={therapist.therapistId}>
                    {therapist.therapistName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Start Date */}
            <TextField
              id="generate-start-date"
              label="Start Date"
              type="date"
              value={generateFromDate}
              onChange={(e) => {
                const selectedDate = e.target.value;
                if (selectedDate && selectedDate <= getTodayDateString()) {
                  setGenerateFromDate(selectedDate);
                } else if (!selectedDate) {
                  setGenerateFromDate('');
                }
              }}
              inputProps={{
                max: getTodayDateString(),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />

            {/* End Date */}
            <TextField
              id="generate-end-date"
              label="End Date"
              type="date"
              value={generateToDate}
              onChange={(e) => {
                const selectedDate = e.target.value;
                if (selectedDate && selectedDate <= getTodayDateString()) {
                  setGenerateToDate(selectedDate);
                } else if (!selectedDate) {
                  setGenerateToDate('');
                }
              }}
              inputProps={{
                max: getTodayDateString(),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant={'outlined'} onClick={handleCloseGenerateModal}>Cancel</Button>
          <Button variant={'contained'} onClick={handleGenerateReportSubmit}>
            {buttonLoadingGenRep ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Generate'}
          </Button>
        </DialogActions>
        </Box>
    </Dialog>

    {/* EMAIL MODAL */}
    <Dialog
        open={openEmail}
        onClose={handleCloseEmail}
        maxWidth={"md"}
        fullWidth={true}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <DialogTitle 
          sx={{ 
            pb: 1,
            fontSize: '1.5rem',
            fontWeight: 600,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          Send Email
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* To Field */}
            <TextField
              autoFocus
              id="emailAddresses"
              name="emailAddresses"
              label="To"
              placeholder="Enter email addresses separated by comma"
              fullWidth
              variant="outlined"
              onChange={handleChangeSendEmail}
              value={formDataEmail.emailAddressesString}
              error={isSubmittedSendEmail && (!formDataEmail.emailAddresses || (formDataEmail.emailAddresses.length > 0 && !validateEmails(formDataEmail.emailAddresses)))}
              helperText={
                isSubmittedSendEmail && (!formDataEmail.emailAddresses || (formDataEmail.emailAddresses.length > 0 && !validateEmails(formDataEmail.emailAddresses)))
                  ? "Please enter valid email address(es)"
                  : "Separate multiple emails with commas"
              }
              sx={{
                pt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />

            {/* CC Field */}
            <TextField
              id="ccAddresses"
              name="ccAddresses"
              label="CC (Optional)"
              placeholder="Enter CC email addresses separated by comma"
              fullWidth
              variant="outlined"
              onChange={handleChangeSendEmail}
              value={formDataEmail.ccAddressesString}
              error={isSubmittedSendEmail && formDataEmail.ccAddresses && formDataEmail.ccAddresses.length > 0 && !validateEmails(formDataEmail.ccAddresses)}
              helperText={
                isSubmittedSendEmail && formDataEmail.ccAddresses && formDataEmail.ccAddresses.length > 0 && !validateEmails(formDataEmail.ccAddresses)
                  ? "Please enter valid email address(es)"
                  : "Separate multiple emails with commas"
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />

            {/* Subject Field */}
            <TextField
              id="subject"
              name="subject"
              label="Subject"
              placeholder="Enter email subject"
              fullWidth
              variant="outlined"
              onChange={handleChangeSendEmail}
              value={formDataEmail.subject}
              error={isSubmittedSendEmail && !formDataEmail.subject}
              helperText={isSubmittedSendEmail && !formDataEmail.subject ? "Subject is required" : ""}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />

            {/* Message Field */}
            <TextField
              id="message"
              name="message"
              label="Message"
              placeholder="Enter your message"
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              onChange={handleChangeSendEmail}
              value={formDataEmail.message}
              error={isSubmittedSendEmail && !formDataEmail.message}
              helperText={isSubmittedSendEmail && !formDataEmail.message ? "Message is required" : ""}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions 
          sx={{ 
            px: 3,
            pb: 2.5,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            gap: 1.5,
          }}
        >
          <Button 
            variant="outlined" 
            onClick={handleCloseEmail}
            sx={{
              minWidth: '100px',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitEmail}
            disabled={buttonLoading}
            sx={{
              minWidth: '100px',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            {buttonLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
    
  );
}

export default ReportComp;
